import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { MaterialCatalog } from '../types';
import {
  ArrowLeft, Search, Plus, Trash2, Save, X, Upload, Download,
  BarChart3, ShoppingBag, TrendingDown, TrendingUp, Star, Package,
  RefreshCw, Edit3, FileText, AlertTriangle
} from 'lucide-react';
import { LoadingSpinner, EmptyState } from '../components/ui';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { usePriceComparatorData, type ViewMode } from '../hooks/usePriceComparatorData';
import { usePriceComparatorActions } from '../hooks/usePriceComparatorActions';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default function PriceComparator() {
  const navigate = useNavigate();
  const data = usePriceComparatorData();
  const {
    loading,
    suppliers, catalog,
    viewMode, setViewMode,
    searchQuery, setSearchQuery, searchResults, searching, setSearchResults, setSearching,
    showAddForm, setShowAddForm,
    newMaterial, setNewMaterial,
    filterBrand, setFilterBrand, filterCategory, setFilterCategory,
    catalogSearch, setCatalogSearch,
    editingPriceFor, setEditingPriceFor,
    priceForm, setPriceForm,
    historyMaterialId, setHistoryMaterialId,
    setPriceHistoryData,
    basket, setBasket,
    basketPrices,
    importing, setImporting,
    blMaterialId, setBlMaterialId,
    blReferences, setBlReferences,
    fetchData,
    brands, categories, filteredCatalog,
    historyChartData, inflationIndicators,
    basketTotal, basketMaxTotal,
  } = data;

  const {
    handleSearch,
    handleAddMaterial,
    handleDeleteMaterial,
    handleSavePrice,
    loadHistory,
    loadBLReferences,
    handleImportCatalog,
    handleImportPrices,
    exportCatalog,
  } = usePriceComparatorActions({
    catalog, suppliers,
    searchQuery, setSearchResults, setSearching,
    newMaterial, setNewMaterial, setShowAddForm,
    priceForm, setPriceForm, setEditingPriceFor,
    historyMaterialId, setHistoryMaterialId, setPriceHistoryData,
    blMaterialId, setBlMaterialId, setBlReferences,
    setImporting, fetchData,
  });

  // ─── PANIER : helpers locaux ─────────────────────────────────────────
  const addToBasket = (material: MaterialCatalog) => {
    if (basket.find(b => b.material.id === material.id)) return;
    setBasket([...basket, { material, quantity: 1 }]);
  };

  const removeFromBasket = (materialId: string) => {
    setBasket(basket.filter(b => b.material.id !== materialId));
  };

  const updateBasketQty = (materialId: string, qty: number) => {
    setBasket(basket.map(b => b.material.id === materialId ? { ...b, quantity: qty } : b));
  };

  // ─── STOCK BADGE ─────────────────────────────────────────────────────
  const StockBadge = ({ status }: { status: string }) => {
    const cfg: Record<string, { label: string; color: string; bg: string }> = {
      available: { label: 'En stock', color: '#10b981', bg: '#ecfdf5' },
      low: { label: 'Stock bas', color: '#f59e0b', bg: '#fffbeb' },
      out_of_stock: { label: 'Rupture', color: '#ef4444', bg: '#fef2f2' },
      unknown: { label: '—', color: '#94a3b8', bg: '#f8fafc' }
    };
    const c = cfg[status] || cfg.unknown;
    return <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', color: c.color, background: c.bg }}>{c.label}</span>;
  };

  // ─── RENDU ───────────────────────────────────────────────────────────
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* ─── HEADER GRADIENT ──────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        borderRadius: '0 0 24px 24px',
        padding: '24px 30px 20px',
        marginBottom: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={() => navigate('/')} style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              color: 'white', width: '44px', height: '44px', borderRadius: '12px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s'
            }}>
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.5rem', color: 'white', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <BarChart3 size={28} />
                Comparateur Prix Matériel
              </h1>
              <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                {catalog.length} articles · {suppliers.length} fournisseurs
              </p>
            </div>
          </div>
        </div>

        {/* ONGLETS */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {([
            { key: 'search', label: 'Recherche & Comparaison', icon: <Search size={16} /> },
            { key: 'catalog', label: 'Catalogue', icon: <Package size={16} /> },
            { key: 'basket', label: 'Panier', icon: <ShoppingBag size={16} />, badge: basket.length }
          ] as { key: ViewMode; label: string; icon: React.ReactNode; badge?: number }[]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setViewMode(tab.key)}
              style={{
                padding: '12px 24px', borderRadius: '12px', cursor: 'pointer',
                fontWeight: '600', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '10px',
                transition: 'all 0.25s ease',
                ...(viewMode === tab.key
                  ? { background: 'white', color: '#1e293b', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
                  : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.2)', boxShadow: 'none' }
                )
              }}
            >
              {tab.icon} {tab.label}
              {tab.badge != null && tab.badge > 0 && (
                <span style={{
                  background: viewMode === tab.key ? '#ef4444' : 'rgba(255,255,255,0.2)',
                  color: 'white', padding: '2px 10px', borderRadius: '20px',
                  fontSize: '0.8rem', fontWeight: '700'
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ═══ VUE RECHERCHE ═══ */}
      {viewMode === 'search' && (
        <>
          {/* Barre de recherche */}
          <div style={{ ...cardStyle, display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Search size={20} color="#94a3b8" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Rechercher par référence, désignation ou marque..."
              style={{ flex: 1, padding: '12px', border: 'none', outline: 'none', fontSize: '1rem' }}
            />
            <button onClick={handleSearch} disabled={searching} style={primaryBtnStyle}>
              {searching ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Search size={16} />}
              {searching ? 'Recherche...' : 'Comparer'}
            </button>
          </div>

          {/* Résultats */}
          {searchResults.length === 0 && !searching && searchQuery && (
            <EmptyState message={`Aucun résultat pour "${searchQuery}". Vérifiez l'orthographe ou ajoutez l'article dans le catalogue.`} icon={<Package size={48} />} />
          )}

          {searchResults.map(result => (
            <div key={result.material.id} style={{ ...cardStyle, marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '700', fontSize: '1.05rem', color: '#1e293b' }}>{result.material.reference}</span>
                    {result.material.brand && (
                      <span style={{ background: '#eff6ff', color: '#2563eb', padding: '3px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '700', border: '1px solid #bfdbfe' }}>
                        {result.material.brand}
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' }}>{result.material.designation}</p>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => addToBasket(result.material)} style={smallBtnStyle} title="Ajouter au panier">
                    <ShoppingBag size={15} />
                  </button>
                  <button onClick={() => loadHistory(result.material.id)} style={smallBtnStyle} title="Historique des prix">
                    <BarChart3 size={15} />
                  </button>
                  <button onClick={() => loadBLReferences(result.material.id)} style={smallBtnStyle} title="Voir les BL">
                    <FileText size={15} />
                  </button>
                </div>
              </div>

              {/* Tableau comparatif */}
              {result.prices.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', fontStyle: 'italic' }}>Aucun prix enregistré pour cet article</p>
              ) : (
                <div style={{ borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={thStyle}>Fournisseur</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Prix catalogue</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Prix net</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Remise</th>
                        <th style={{ ...thStyle, textAlign: 'center' }}>Stock</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Délai</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.prices.map(p => {
                        const isBest = p.supplier_id === result.bestSupplierId;
                        return (
                          <tr key={p.id} style={{
                            borderBottom: '1px solid #f1f5f9',
                            background: isBest ? '#f0fdf4' : 'white',
                            transition: 'background 0.15s'
                          }}>
                            <td style={tdStyle}>
                              <span style={{ fontWeight: isBest ? '700' : '500', color: '#1e293b', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                {isBest && <Star size={14} color="#f59e0b" />}
                                {p.supplier_name}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: '#94a3b8' }}>
                              {p.catalog_price ? `${p.catalog_price.toFixed(2)} €` : '—'}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: isBest ? '#10b981' : '#1e293b' }}>
                              {p.net_price ? `${p.net_price.toFixed(2)} €` : '—'}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              {p.discount_percent ? (
                                <span style={{ color: '#10b981', fontWeight: '600' }}>-{p.discount_percent.toFixed(1)}%</span>
                              ) : '—'}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}><StockBadge status={p.stock_status} /></td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              {p.delivery_days ? `${p.delivery_days}j` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Économie potentielle */}
              {result.savings && result.savings > 0 && (
                <div style={{
                  marginTop: '12px', padding: '10px 16px', borderRadius: '10px',
                  background: '#f0fdf4', display: 'flex', alignItems: 'center', gap: '8px'
                }}>
                  <TrendingDown size={18} color="#10b981" />
                  <span style={{ fontSize: '0.85rem', color: '#10b981', fontWeight: '600' }}>
                    Économie potentielle : {result.savings.toFixed(2)} € / unité
                  </span>
                </div>
              )}

              {/* Historique des prix (graphique) */}
              {historyMaterialId === result.material.id && historyChartData && (
                <div style={{ marginTop: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px' }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#475569' }}>Historique des prix</h4>

                  {/* Indicateurs d'inflation */}
                  {inflationIndicators.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                      {inflationIndicators.map((ind, idx) => {
                        const isUp = ind.delta > 5;
                        const isDown = ind.delta < -5;
                        const bg = isUp ? '#fef2f2' : isDown ? '#ecfdf5' : '#f8fafc';
                        const color = isUp ? '#ef4444' : isDown ? '#10b981' : '#64748b';
                        const border = isUp ? '#fecaca' : isDown ? '#86efac' : '#e2e8f0';
                        const Icon = isUp ? TrendingUp : isDown ? TrendingDown : TrendingUp;
                        return (
                          <div key={idx} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '6px 12px', borderRadius: '8px',
                            background: bg, border: `1px solid ${border}`,
                            fontSize: '0.8rem', fontWeight: '600', color
                          }}>
                            {isUp && <AlertTriangle size={14} />}
                            {!isUp && <Icon size={14} />}
                            <span>{ind.supplierName}</span>
                            <span style={{ fontWeight: '700' }}>
                              {ind.delta > 0 ? '+' : ''}{ind.delta}%
                            </span>
                            <span style={{ fontWeight: '400', fontSize: '0.7rem', color: '#94a3b8' }}>
                              ({ind.firstPrice.toFixed(2)}→{ind.lastPrice.toFixed(2)}€)
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div style={{ height: '250px' }}>
                    <Line
                      data={historyChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'top' },
                          tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${(ctx.parsed.y ?? 0).toFixed(2)} €` } }
                        },
                        scales: {
                          y: { title: { display: true, text: 'Prix (€)' }, beginAtZero: false }
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Cross-référence BL */}
              {blMaterialId === result.material.id && (
                <div style={{ marginTop: '12px', padding: '16px', background: '#fffbeb', borderRadius: '12px', border: '1px solid #fde68a' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FileText size={16} /> Bons de livraison contenant cet article
                  </h4>
                  {blReferences.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>Aucun BL trouvé pour cet article</p>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #fde68a' }}>
                            <th style={{ ...thStyle, color: '#92400e' }}>N° BL</th>
                            <th style={{ ...thStyle, color: '#92400e' }}>Date</th>
                            <th style={{ ...thStyle, color: '#92400e' }}>Fournisseur</th>
                            <th style={{ ...thStyle, color: '#92400e' }}>Chantier</th>
                            <th style={{ ...thStyle, textAlign: 'right', color: '#92400e' }}>Qté</th>
                            <th style={{ ...thStyle, textAlign: 'right', color: '#92400e' }}>P.U.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {blReferences.map((bl, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #fef3c7' }}>
                              <td style={{ ...tdStyle, fontWeight: '600', color: '#1e293b' }}>{bl.bl_number}</td>
                              <td style={tdStyle}>{bl.bl_date !== '—' ? new Date(bl.bl_date).toLocaleDateString('fr-FR') : '—'}</td>
                              <td style={tdStyle}>{bl.supplier_name}</td>
                              <td style={tdStyle}>{bl.project_name}</td>
                              <td style={{ ...tdStyle, textAlign: 'right' }}>{bl.quantity}</td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>{bl.unit_price.toFixed(2)} €</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* ═══ VUE CATALOGUE ═══ */}
      {viewMode === 'catalog' && (
        <>
          {/* Actions catalogue */}
          <div style={{
            background: 'white', padding: '16px 20px', borderRadius: '14px', border: '1px solid #e2e8f0',
            display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px'
          }}>
            <button onClick={() => setShowAddForm(!showAddForm)} style={primaryBtnStyle}>
              <Plus size={16} /> Ajouter un article
            </button>
            <label style={{ ...secondaryBtnStyle, cursor: 'pointer' }}>
              <Upload size={16} /> Import catalogue
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImportCatalog} style={{ display: 'none' }} hidden />
            </label>
            <label style={{ ...secondaryBtnStyle, cursor: 'pointer' }}>
              <Upload size={16} /> Import prix
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImportPrices} style={{ display: 'none' }} hidden />
            </label>
            <button onClick={exportCatalog} style={secondaryBtnStyle}>
              <Download size={16} /> Export
            </button>
            {importing && <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '0.85rem' }}>Import en cours...</span>}
            <span style={{ marginLeft: 'auto', fontSize: '0.82rem', color: '#94a3b8', fontWeight: '600' }}>
              {filteredCatalog.length} articles
            </span>
          </div>

          {/* Formulaire ajout article */}
          {showAddForm && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 16px', fontSize: '1rem', color: '#1e293b', fontWeight: '700' }}>Nouvel article</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>Référence *</label>
                  <input value={newMaterial.reference} onChange={e => setNewMaterial({ ...newMaterial, reference: e.target.value })} style={inputStyle} placeholder="LEG-04886" />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={labelStyle}>Désignation *</label>
                  <input value={newMaterial.designation} onChange={e => setNewMaterial({ ...newMaterial, designation: e.target.value })} style={inputStyle} placeholder="Disjoncteur DX3 10A" />
                </div>
                <div>
                  <label style={labelStyle}>Marque</label>
                  <input value={newMaterial.brand} onChange={e => setNewMaterial({ ...newMaterial, brand: e.target.value })} style={inputStyle} placeholder="Legrand" />
                </div>
                <div>
                  <label style={labelStyle}>Catégorie</label>
                  <input value={newMaterial.category} onChange={e => setNewMaterial({ ...newMaterial, category: e.target.value })} style={inputStyle} placeholder="Protection" />
                </div>
                <div>
                  <label style={labelStyle}>Unité</label>
                  <input value={newMaterial.unit} onChange={e => setNewMaterial({ ...newMaterial, unit: e.target.value })} style={inputStyle} placeholder="pce" />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                <button onClick={() => setShowAddForm(false)} style={secondaryBtnStyle}><X size={14} /> Annuler</button>
                <button onClick={handleAddMaterial} style={primaryBtnStyle}><Save size={14} /> Enregistrer</button>
              </div>
            </div>
          )}

          {/* Filtres catalogue */}
          <div style={{
            background: 'white', padding: '16px 20px', borderRadius: '14px', border: '1px solid #e2e8f0',
            display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '16px'
          }}>
            <div>
              <label style={labelStyle}><Search size={12} /> Recherche</label>
              <input value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)} placeholder="Réf, désignation, marque..." style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Marque</label>
              <select value={filterBrand} onChange={e => setFilterBrand(e.target.value)} style={inputStyle}>
                <option value="all">Toutes</option>
                {brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Catégorie</label>
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={inputStyle}>
                <option value="all">Toutes</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Tableau catalogue */}
          {filteredCatalog.length === 0 ? (
            <div style={{
              background: 'linear-gradient(145deg, #ffffff, #f8fafc)', borderRadius: '24px',
              border: '2px dashed #e2e8f0', padding: '80px 40px', textAlign: 'center', color: '#94a3b8'
            }}>
              <Package size={64} style={{ marginBottom: '16px', opacity: 0.3 }} />
              <p style={{ fontWeight: '600', fontSize: '1rem', color: '#64748b' }}>Catalogue vide</p>
              <p style={{ fontSize: '0.85rem' }}>Importez un fichier Excel ou ajoutez des articles manuellement.</p>
            </div>
          ) : (
            <div style={{
              background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0',
              overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.03)'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ ...thStyle, width: '140px' }}>Référence</th>
                    <th style={thStyle}>Désignation</th>
                    <th style={{ ...thStyle, textAlign: 'center', width: '120px' }}>Marque</th>
                    <th style={{ ...thStyle, textAlign: 'center', width: '120px' }}>Catégorie</th>
                    <th style={{ ...thStyle, textAlign: 'center', width: '160px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCatalog.map((m, idx) => (
                    <React.Fragment key={m.id}>
                      <tr
                        style={{
                          borderBottom: idx < filteredCatalog.length - 1 ? '1px solid #f1f5f9' : 'none',
                          transition: 'background 0.15s'
                        }}
                        onMouseOver={e => { e.currentTarget.style.background = '#fafbfc'; }}
                        onMouseOut={e => { e.currentTarget.style.background = 'white'; }}
                      >
                        <td style={tdStyle}>
                          <span style={{ fontWeight: '700', color: '#1e293b', fontFamily: 'monospace', fontSize: '0.82rem' }}>{m.reference}</span>
                        </td>
                        <td style={{ ...tdStyle, color: '#475569' }}>{m.designation}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {m.brand ? (
                            <span style={{ background: '#eff6ff', color: '#2563eb', padding: '3px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '700', border: '1px solid #bfdbfe' }}>
                              {m.brand}
                            </span>
                          ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {m.category ? (
                            <span style={{ background: '#f8fafc', color: '#64748b', padding: '3px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: '600', border: '1px solid #e2e8f0' }}>
                              {m.category}
                            </span>
                          ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button onClick={() => {
                              setEditingPriceFor(editingPriceFor === m.id ? null : m.id);
                              setPriceForm({ supplier_id: '', catalog_price: '', net_price: '', stock_status: 'unknown', delivery_days: '' });
                            }} style={{ ...smallBtnStyle, background: editingPriceFor === m.id ? '#eff6ff' : '#f1f5f9', color: editingPriceFor === m.id ? '#2563eb' : '#64748b' }} title="Ajouter un prix">
                              <Edit3 size={14} />
                            </button>
                            <button onClick={() => addToBasket(m)} style={smallBtnStyle} title="Ajouter au panier">
                              <ShoppingBag size={14} />
                            </button>
                            <button onClick={() => loadBLReferences(m.id)} style={smallBtnStyle} title="Voir les BL">
                              <FileText size={14} />
                            </button>
                            <button onClick={() => handleDeleteMaterial(m.id)} style={{ ...smallBtnStyle, color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca' }} title="Supprimer">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Formulaire ajout prix — ligne dépliable */}
                      {editingPriceFor === m.id && (
                        <tr>
                          <td colSpan={5} style={{ padding: 0 }}>
                            <div style={{
                              background: '#f8fafc', padding: '16px 20px',
                              borderTop: '2px dashed #e2e8f0', borderBottom: '2px dashed #e2e8f0'
                            }}>
                              <h4 style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#1e293b', fontWeight: '700' }}>
                                Ajouter un prix — {m.reference}
                              </h4>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
                                <div>
                                  <label style={labelStyle}>Fournisseur</label>
                                  <select value={priceForm.supplier_id} onChange={e => setPriceForm({ ...priceForm, supplier_id: e.target.value })} style={inputStyle}>
                                    <option value="">— Fournisseur —</option>
                                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label style={labelStyle}>Prix catalogue</label>
                                  <input type="number" placeholder="0.00" value={priceForm.catalog_price} onChange={e => setPriceForm({ ...priceForm, catalog_price: e.target.value })} style={inputStyle} />
                                </div>
                                <div>
                                  <label style={labelStyle}>Prix net</label>
                                  <input type="number" placeholder="0.00" value={priceForm.net_price} onChange={e => setPriceForm({ ...priceForm, net_price: e.target.value })} style={inputStyle} />
                                </div>
                                <div>
                                  <label style={labelStyle}>Stock</label>
                                  <select value={priceForm.stock_status} onChange={e => setPriceForm({ ...priceForm, stock_status: e.target.value })} style={inputStyle}>
                                    <option value="unknown">Inconnu</option>
                                    <option value="available">En stock</option>
                                    <option value="low">Stock bas</option>
                                    <option value="out_of_stock">Rupture</option>
                                  </select>
                                </div>
                                <div>
                                  <label style={labelStyle}>Délai (jours)</label>
                                  <input type="number" placeholder="0" value={priceForm.delivery_days} onChange={e => setPriceForm({ ...priceForm, delivery_days: e.target.value })} style={inputStyle} />
                                </div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
                                <button onClick={() => setEditingPriceFor(null)} style={secondaryBtnStyle}><X size={14} /> Annuler</button>
                                <button onClick={() => handleSavePrice(m.id)} style={primaryBtnStyle}><Save size={14} /> Enregistrer</button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Cross-référence BL — ligne dépliable */}
                      {blMaterialId === m.id && (
                        <tr>
                          <td colSpan={5} style={{ padding: 0 }}>
                            <div style={{
                              background: '#fffbeb', padding: '14px 20px',
                              borderTop: '1px solid #fde68a', borderBottom: '1px solid #fde68a'
                            }}>
                              <h5 style={{ margin: '0 0 10px', fontSize: '0.82rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700' }}>
                                <FileText size={14} /> BL contenant {m.reference}
                              </h5>
                              {blReferences.length === 0 ? (
                                <p style={{ color: '#94a3b8', fontSize: '0.82rem', fontStyle: 'italic', margin: 0 }}>Aucun BL trouvé</p>
                              ) : (
                                <div style={{ borderRadius: '8px', border: '1px solid #fde68a', overflow: 'hidden' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr style={{ background: '#fef3c7' }}>
                                        <th style={{ ...thStyle, color: '#92400e', padding: '10px 12px' }}>N BL</th>
                                        <th style={{ ...thStyle, color: '#92400e', padding: '10px 12px' }}>Date</th>
                                        <th style={{ ...thStyle, color: '#92400e', padding: '10px 12px' }}>Fournisseur</th>
                                        <th style={{ ...thStyle, color: '#92400e', padding: '10px 12px' }}>Chantier</th>
                                        <th style={{ ...thStyle, color: '#92400e', padding: '10px 12px', textAlign: 'right' }}>Qté</th>
                                        <th style={{ ...thStyle, color: '#92400e', padding: '10px 12px', textAlign: 'right' }}>P.U.</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {blReferences.map((bl, blIdx) => (
                                        <tr key={blIdx} style={{ borderBottom: blIdx < blReferences.length - 1 ? '1px solid #fef3c7' : 'none', background: 'white' }}>
                                          <td style={{ ...tdStyle, fontWeight: '600', color: '#1e293b', padding: '10px 12px' }}>{bl.bl_number}</td>
                                          <td style={{ ...tdStyle, padding: '10px 12px' }}>{bl.bl_date !== '—' ? new Date(bl.bl_date).toLocaleDateString('fr-FR') : '—'}</td>
                                          <td style={{ ...tdStyle, padding: '10px 12px' }}>{bl.supplier_name}</td>
                                          <td style={{ ...tdStyle, padding: '10px 12px' }}>{bl.project_name}</td>
                                          <td style={{ ...tdStyle, textAlign: 'right', padding: '10px 12px' }}>{bl.quantity}</td>
                                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600', padding: '10px 12px' }}>{bl.unit_price.toFixed(2)} €</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ═══ VUE PANIER ═══ */}
      {viewMode === 'basket' && (
        <>
          {basket.length === 0 ? (
            <div style={{
              background: 'linear-gradient(145deg, #ffffff, #f8fafc)', borderRadius: '24px',
              border: '2px dashed #e2e8f0', padding: '80px 40px', textAlign: 'center', color: '#94a3b8'
            }}>
              <ShoppingBag size={64} style={{ marginBottom: '16px', opacity: 0.3 }} />
              <p style={{ fontWeight: '600', fontSize: '1rem', color: '#64748b' }}>Votre panier est vide</p>
              <p style={{ fontSize: '0.85rem' }}>Recherchez des articles et ajoutez-les au panier pour obtenir le prix optimal</p>
            </div>
          ) : (
            <>
              {/* Stats panier */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div style={{ ...cardStyle, textAlign: 'center', background: 'linear-gradient(145deg, #ecfdf5, #d1fae5)', border: '1px solid #86efac' }}>
                  <div style={{ fontSize: '0.82rem', color: '#047857', marginBottom: '6px', fontWeight: '600' }}>Prix optimal</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#047857' }}>{basketTotal.toFixed(2)} €</div>
                </div>
                {basketMaxTotal > basketTotal && (
                  <div style={{ ...cardStyle, textAlign: 'center', background: 'linear-gradient(145deg, #fffbeb, #fef3c7)', border: '1px solid #fcd34d' }}>
                    <div style={{ fontSize: '0.82rem', color: '#b45309', marginBottom: '6px', fontWeight: '600' }}>Économie vs prix max</div>
                    <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#b45309', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <TrendingDown size={20} /> {(basketMaxTotal - basketTotal).toFixed(2)} €
                    </div>
                  </div>
                )}
                <div style={{ ...cardStyle, textAlign: 'center', background: 'linear-gradient(145deg, #eff6ff, #dbeafe)', border: '1px solid #93c5fd' }}>
                  <div style={{ fontSize: '0.82rem', color: '#1d4ed8', marginBottom: '6px', fontWeight: '600' }}>Articles</div>
                  <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#1d4ed8' }}>{basket.length}</div>
                </div>
              </div>

              {/* Liste panier */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {basket.map(item => {
                  const compared = basketPrices.find(bp => bp.material.id === item.material.id);
                  return (
                    <div key={item.material.id} style={{ ...cardStyle, marginBottom: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div>
                          <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '1rem' }}>{item.material.reference}</span>
                          <span style={{ marginLeft: '10px', color: '#64748b', fontSize: '0.88rem' }}>{item.material.designation}</span>
                        </div>
                        <button onClick={() => removeFromBasket(item.material.id)} style={{ ...smallBtnStyle, color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <label style={labelStyle}>Quantité</label>
                        <input
                          type="number" min="1" value={item.quantity}
                          onChange={e => updateBasketQty(item.material.id, parseInt(e.target.value) || 1)}
                          style={{ ...inputStyle, width: '80px', textAlign: 'right' as const }}
                        />
                        {compared?.bestPrice && (
                          <span style={{
                            fontWeight: '700', color: '#10b981', fontSize: '0.88rem',
                            background: '#ecfdf5', padding: '6px 14px', borderRadius: '10px', border: '1px solid #86efac'
                          }}>
                            {compared.bestPrice.toFixed(2)} € × {item.quantity} = {(compared.bestPrice * item.quantity).toFixed(2)} €
                          </span>
                        )}
                      </div>

                      {/* Mini tableau comparatif */}
                      {compared && compared.prices.length > 0 && (
                        <div style={{ borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={thStyle}>Fournisseur</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Prix net</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                                <th style={thStyle}>Stock</th>
                              </tr>
                            </thead>
                            <tbody>
                              {compared.prices.map(p => {
                                const isBest = p.supplier_id === compared.bestSupplierId;
                                const price = p.net_price || p.catalog_price || 0;
                                return (
                                  <tr key={p.id} style={{ background: isBest ? '#f0fdf4' : 'white', borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}>
                                    <td style={tdStyle}>
                                      <span style={{ fontWeight: isBest ? '700' : '500', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                        {isBest && <Star size={12} color="#f59e0b" />}
                                        {p.supplier_name}
                                      </span>
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: isBest ? '700' : '400', color: isBest ? '#10b981' : '#1e293b' }}>
                                      {price.toFixed(2)} €
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                                      {(price * item.quantity).toFixed(2)} €
                                    </td>
                                    <td style={tdStyle}><StockBadge status={p.stock_status} /></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {(!compared || compared.prices.length === 0) && (
                        <p style={{ color: '#94a3b8', fontSize: '0.8rem', fontStyle: 'italic' }}>Aucun prix enregistré</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────
const cardStyle: React.CSSProperties = {
  background: 'white', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
  border: '1px solid #e2e8f0', padding: '24px', marginBottom: '16px', position: 'relative'
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1',
  fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box'
};
const labelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '4px',
  fontSize: '0.75rem', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em'
};
const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '10px 18px', borderRadius: '12px', border: 'none', background: '#2563eb', color: 'white',
  fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s'
};
const secondaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '10px 18px', borderRadius: '12px', border: '1px solid #e2e8f0', background: 'white', color: '#475569',
  fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s'
};
const smallBtnStyle: React.CSSProperties = {
  background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b',
  width: '34px', height: '34px', borderRadius: '10px', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
};
const thStyle: React.CSSProperties = {
  padding: '14px 12px', fontWeight: '700', fontSize: '0.72rem', textAlign: 'left',
  color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em'
};
const tdStyle: React.CSSProperties = { padding: '14px 12px', fontSize: '0.85rem' };
